import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useUsers } from '../hooks/useUsers';

const getRoleLabels = (t) => ({
  admin: t('roles.admin'),
  engineer: t('roles.engineer'),
  viewer: t('admin.viewer'),
  demo: t('roles.demo'),
});

const roleColors = {
  admin: 'bg-critical/20 text-critical border-critical/40',
  engineer: 'bg-brand-400/20 text-brand-200 border-brand-400/40',
  viewer: 'bg-accent-cyan/20 text-accent-cyan border-accent-cyan/40',
  demo: 'bg-warning/20 text-warning border-warning/40',
};

const ROLE_OPTIONS = ['viewer', 'demo', 'engineer', 'admin'];

const SchoolMultiSelect = ({ objects, selectedIds, onChange, disabled, label, placeholder }) => {
  const selected = useMemo(() => new Set((selectedIds || []).map(Number)), [selectedIds]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next].sort((a, b) => a - b));
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-muted-300 mb-2">{label}</label>}
      <div className="max-h-48 overflow-y-auto rounded-2xl border border-surface-border bg-surface-subtle/40 p-2 space-y-1">
        {objects.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-400">{placeholder}</p>
        ) : (
          objects.map((obj) => {
            const checked = selected.has(obj.id);
            return (
              <label
                key={obj.id}
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${
                  checked ? 'bg-brand-500/15 text-white' : 'text-muted-200 hover:bg-white/[0.04]'
                } ${disabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(obj.id)}
                  className="rounded border-surface-border bg-surface-raised text-brand-500 focus:ring-brand-400"
                />
                <span className="truncate">{obj.name}</span>
                {(obj.city || obj.region) && (
                  <span className="ml-auto truncate text-xs text-muted-500">
                    {[obj.city, obj.region].filter(Boolean).join(' · ')}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const DemoSchoolsEditor = ({ user, objects, onSave, t }) => {
  const [draftIds, setDraftIds] = useState(() => user.objectIds || []);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraftIds(user.objectIds || []);
    setDirty(false);
  }, [user.id, user.objectIds]);

  return (
    <div className="mt-4 space-y-3">
      <SchoolMultiSelect
        objects={objects}
        selectedIds={draftIds}
        onChange={(objectIds) => {
          setDraftIds(objectIds);
          setDirty(true);
        }}
        label={t('admin.demoSchools')}
        placeholder={t('admin.noSchools')}
      />
      {dirty && (
        <button
          type="button"
          onClick={() => onSave(draftIds)}
          className="px-4 py-2 rounded-xl border border-brand-400/40 bg-brand-500/15 text-brand-200 text-sm hover:bg-brand-500/25 transition"
        >
          {t('admin.saveSchools')}
        </button>
      )}
    </div>
  );
};

const AdminPanel = ({ setIsAuthenticated, userRole, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roleLabels = getRoleLabels(t);
  const {
    users,
    objects,
    addUser,
    updateUser,
    updateUserSchools,
    deleteUser,
    error,
    success,
    loading,
    setError,
    setSuccess,
  } = useUsers();

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'viewer',
    objectIds: [],
  });
  const [isSidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success, setSuccess]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error, setError]);

  const toggleSidebar = () => setSidebarVisible(!isSidebarVisible);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password) {
      return setError(t('admin.emailPasswordRequired'));
    }
    if (newUser.role === 'demo' && (!newUser.objectIds || newUser.objectIds.length === 0)) {
      return setError(t('admin.demoSchoolsRequired'));
    }
    const payload = {
      email: newUser.email,
      password: newUser.password,
      role: newUser.role,
    };
    if (newUser.role === 'demo') {
      payload.objectIds = newUser.objectIds;
    }
    addUser(payload);
    setNewUser({ email: '', password: '', role: 'viewer', objectIds: [] });
  };

  const handleRoleChange = (user, role) => {
    // Switching to demo keeps existing school allowlist (possibly empty until admin saves schools).
    updateUser(user.id, role, role === 'demo' ? (user.objectIds || []) : undefined);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    navigate('/');
  };

  const userEmail = localStorage.getItem('userEmail') || '';
  const objectNameById = useMemo(() => {
    const map = new Map();
    objects.forEach((obj) => map.set(obj.id, obj.name));
    return map;
  }, [objects]);

  return (
    <div className="flex-1 flex flex-col min-h-screen">
    <Header isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} userEmail={userEmail} userRole={userRole} />
      <div className="flex flex-1 min-h-0">
        <Sidebar 
          onLogout={onLogout || handleLogout} 
          userRole={userRole} 
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />

        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-white mb-2 font-display">{t('admin.title')}</h1>
              <p className="text-muted-300">{t('admin.subtitle')}</p>
            </div>

            {error && (
              <div className="mb-6 rounded-2.5xl border border-critical/50 bg-critical/10 p-4 flex items-center gap-3 animate-in slide-in-from-top-2">
                <span className="material-icons text-critical text-xl">error_outline</span>
                <p className="text-critical flex-1">{error}</p>
                <button
                  onClick={() => setError('')}
                  className="text-critical hover:text-critical/70 transition"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}

            {success && (
              <div className="mb-6 rounded-2.5xl border border-positive/50 bg-positive/10 p-4 flex items-center gap-3 animate-in slide-in-from-top-2">
                <span className="material-icons text-positive text-xl">check_circle</span>
                <p className="text-positive flex-1">{success}</p>
                <button
                  onClick={() => setSuccess('')}
                  className="text-positive hover:text-positive/70 transition"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}

            <div className="mb-8 rounded-3xl border border-surface-border bg-surface-raised/60 backdrop-blur-xl p-6 shadow-elevated">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-icons text-brand-400 text-2xl">person_add</span>
                <h2 className="text-2xl font-semibold text-white font-display">{t('admin.addUser')}</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-300 mb-2">{t('admin.email')}</label>
              <input
                type="email"
                      placeholder="user@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white placeholder-muted-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                required
              />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-300 mb-2">{t('admin.password')}</label>
              <input
                type="password"
                      placeholder="••••••••"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white placeholder-muted-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                required
              />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-300 mb-2">{t('admin.defaultRole')}</label>
              <select
                value={newUser.role}
                onChange={(e) => {
                  const role = e.target.value;
                  setNewUser({
                    ...newUser,
                    role,
                    objectIds: role === 'demo' ? newUser.objectIds : [],
                  });
                }}
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
              >
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {roleLabels[role] || role}
                        </option>
                      ))}
              </select>
                  </div>
            </div>

            {newUser.role === 'demo' && (
              <SchoolMultiSelect
                objects={objects}
                selectedIds={newUser.objectIds}
                onChange={(objectIds) => setNewUser({ ...newUser, objectIds })}
                label={t('admin.demoSchools')}
                placeholder={t('admin.noSchools')}
              />
            )}

            <button
              type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-brand-500 to-brand-600 text-white font-semibold rounded-2xl hover:from-brand-600 hover:to-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-raised transition shadow-brand"
            >
                  <span className="flex items-center gap-2">
                    <span className="material-icons">add</span>
              {t('admin.addUserButton')}
                  </span>
            </button>
          </form>
            </div>

            <div className="rounded-3xl border border-surface-border bg-surface-raised/60 backdrop-blur-xl p-6 shadow-elevated">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="material-icons text-brand-400 text-2xl">people</span>
                  <h2 className="text-2xl font-semibold text-white font-display">{t('admin.users')}</h2>
                  {loading && (
                    <span className="ml-4 text-muted-400 text-sm flex items-center gap-2">
                      <span className="material-icons animate-spin text-lg">refresh</span>
                      {t('common.loading')}
                    </span>
                  )}
                </div>
                <span className="text-muted-400 text-sm">{t('admin.totalUsers', { count: users.length })}</span>
              </div>

              {users.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <span className="material-icons text-6xl text-muted-600 mb-4">people_outline</span>
                  <p className="text-muted-400">{t('admin.noUsers')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {users.map((user) => {
                    return (
                      <div
                        key={user.id}
                        className="rounded-2.5xl border border-surface-border bg-surface-subtle/40 p-5 transition hover:border-brand-400/40 hover:bg-surface-subtle/60"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-semibold text-lg">
                                {user.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-white">{user.email}</h3>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${roleColors[user.role] || roleColors.viewer}`}>
                                    {roleLabels[user.role] || user.role}
                                  </span>
                                  {user.role === 'demo' && (user.objectIds || []).length > 0 && (
                                    <span className="text-xs text-muted-400">
                                      {(user.objectIds || [])
                                        .map((id) => objectNameById.get(id) || `#${id}`)
                                        .join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user, e.target.value)}
                              className="px-4 py-2 rounded-xl border border-surface-border bg-surface-raised/60 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 transition"
                            >
                              {ROLE_OPTIONS.map((role) => (
                                <option key={role} value={role}>
                                  {roleLabels[role] || role}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => deleteUser(user.id)}
                              className="px-4 py-2 rounded-xl border border-critical/50 bg-critical/10 text-critical hover:bg-critical/20 transition"
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          </div>
                        </div>

                        {user.role === 'demo' && (
                          <DemoSchoolsEditor
                            user={user}
                            objects={objects}
                            onSave={(objectIds) => updateUserSchools(user.id, objectIds)}
                            t={t}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

    </div>
  );
};

export default AdminPanel;
